import { MutationTree, GetterTree, ActionTree } from "vuex";
import { RootState } from "vuefront-store";
import gql from "graphql-tag";
import type { ViewportMode } from "../../../utils/viewportMode";
import {
  normalizeCatalogHydration,
  type CatalogHydrationMetadata,
} from "../../../utils/catalogHydration";
import {
  enforceAccountRoute,
  installAccountRouteGuards,
} from "../../../account/routes";
export type State = {
  error: boolean | string;
  ssr: boolean;
  sidebar: boolean;
  sidebarCart: boolean;
  version: string;
  flagForSidebarCart: boolean;
  resolvedSeoRoute: {
    type: string | null;
    id: number | null;
    path: string | null;
    notFound: boolean;
  };
  /**
   * Set only when an SSR entity query SUCCEEDED and returned nothing, so the
   * server can answer 404 for a genuinely missing record. A failed request
   * must never land here: a transient API error would otherwise be published
   * to crawlers as a permanent 404.
   */
  ssrEntityMissing: boolean;
  /** Product-free metadata for the catalog page already rendered by SSR. */
  catalogHydration: CatalogHydrationMetadata | null;
  /** Shared with the client so randomized SSR product strips keep their order. */
  hydrationSeed: number;
  hydrationViewport: ViewportMode;
};
export const state = (): State => ({
  error: false,
  ssr: false,
  sidebar: false,
  sidebarCart: false,
  version: "1.0.0",
  flagForSidebarCart: false,
  resolvedSeoRoute: {
    type: null,
    id: null,
    path: null,
    notFound: false,
  },
  ssrEntityMissing: false,
  catalogHydration: null,
  hydrationSeed: Math.floor(Math.random() * 0x100000000),
  hydrationViewport: "desktop",
});

export const mutations: MutationTree<State> = {
  setHydrationViewport(state, mode: ViewportMode) {
    state.hydrationViewport = mode;
  },
  toggleSidebar(state) {
    state.sidebar = !state.sidebar;
  },
  toggleSidebarCart(state) {
    state.sidebarCart = !state.sidebarCart;
  },
  setSidebarCart(state, payload) {
    state.sidebarCart = payload;
  },
  setFlagForSidebarCart(state, payload) {
    state.flagForSidebarCart = payload;
  },
  setResponseError(state, e) {
    if (e.graphQLErrors && e.graphQLErrors.message) {
      state.error = e.graphQLErrors.message;
    } else if (e.graphQLErrors && typeof e.graphQLErrors[0] !== "undefined") {
      state.error = e.graphQLErrors[0].message;
    } else if (e.graphQLErrors) {
      state.error = e.graphQLErrors;
    } else {
      state.error = e;
    }
  },
  setError(state, payload) {
    state.error = payload;
  },
  setSSR(state, payload) {
    state.ssr = payload;
  },
  setVersion(state, payload) {
    state.version = payload;
  },
  setResolvedSeoRoute(state, { type, id, path = null }) {
    state.resolvedSeoRoute.type = type;
    state.resolvedSeoRoute.id = id;
    state.resolvedSeoRoute.path = path;
    state.resolvedSeoRoute.notFound = false;
  },
  setResolvedSeoRouteNotFound(state, { path = null }) {
    state.resolvedSeoRoute.type = null;
    state.resolvedSeoRoute.id = null;
    state.resolvedSeoRoute.path = path;
    state.resolvedSeoRoute.notFound = true;
  },
  clearResolvedSeoRoute(state) {
    state.resolvedSeoRoute.type = null;
    state.resolvedSeoRoute.id = null;
    state.resolvedSeoRoute.path = null;
    state.resolvedSeoRoute.notFound = false;
  },
  setSsrEntityMissing(state, payload) {
    state.ssrEntityMissing = payload === true;
  },
  setCatalogHydration(state, payload) {
    state.catalogHydration = normalizeCatalogHydration(payload);
  },
};

export const getters: GetterTree<State, RootState> = {
  sidebar(state) {
    return state.sidebar;
  },
  sidebarCart(state) {
    return state.sidebarCart;
  },
  flagForSidebarCart(state) {
    return state.flagForSidebarCart;
  },
  error(state) {
    return state.error;
  },
  ssr(state) {
    return state.ssr;
  },
  version(state) {
    return state.version;
  },
  catalogHydration(state) {
    return normalizeCatalogHydration(state.catalogHydration);
  },
};

export const actions: ActionTree<State, RootState> = {
  async checkVersion({ commit }) {
    try {
      const { data } = await this.$vfapollo.query({
        query: gql`
          {
            version
          }
        `,
      });
      commit("setVersion", data.version);
    } catch (e) {
      commit("setVersion", this.$vuefront.version.api);
    }
  },
  async vuefrontInit({ dispatch, commit, getters }) {
    installAccountRouteGuards(this.$router, this);
    await dispatch("checkVersion");
    const requiredApiVersion = String(this.$vuefront.version.api || "0.0.0");
    const currentApiVersion = String(getters["version"] || requiredApiVersion);
    const isDeprecatedApi =
      compareVersions(currentApiVersion, requiredApiVersion) < 0;

    if (isDeprecatedApi) {
      throw new Error(
        "The storefront API is temporarily incompatible with this release."
      );
    }
    if (typeof window === "undefined") {
      // The server-side GraphQL guard deliberately permits only two distinct
      // anonymous upstream requests at once. Respect that bound during a cold
      // bootstrap: locale/currency affect the rendered catalog, then session
      // state can load without two silently rejected Apollo operations.
      await Promise.all([
        dispatch("common/language/load", {}, { root: true }),
        dispatch("store/currency/load", {}, { root: true }),
      ]);
      await Promise.all([
        dispatch("store/cart/load", {}, { root: true }),
        dispatch("common/customer/checkLogged", {}, { root: true }),
      ]);
    } else {
      await Promise.all([
        dispatch("store/cart/load", {}, { root: true }),
        dispatch("common/language/load", {}, { root: true }),
        dispatch("common/customer/checkLogged", {}, { root: true }),
        dispatch("store/currency/load", {}, { root: true }),
      ]);
    }

    if (this.$cookies.get("mode")) {
      commit("store/category/setMode", this.$cookies.get("mode"), {
        root: true,
      });
    }
  },
  async nuxtServerInit({ dispatch, commit }) {
    await dispatch("vuefrontInit");
    commit("setSSR", true);
  },
  async nuxtClientInit({ commit, dispatch, rootGetters }) {
    installAccountRouteGuards(this.$router, this);
    this.$router.beforeEach((to, from, next) => {
      if (to.path !== from.path && rootGetters["vuefront/sidebar"]) {
        commit("toggleSidebar");
      }
      next();
    });
    if (!rootGetters["vuefront/ssr"]) {
      await dispatch("vuefrontInit");
    } else {
      // SSR cart data is only a render-time snapshot. Reconcile it once the
      // browser owns the session so cached/stale SSR state cannot hide items
      // that are still present in the server-side cart.
      await dispatch("store/cart/load", {}, { root: true });
      if (rootGetters["common/customer/authState"] === "checking") {
        await dispatch("common/customer/checkLogged", {}, { root: true });
      }
    }

    // Vue installs its initial browser route when app.use(router) runs, before
    // this store action can register guards. Enforce that one already-active
    // route once after session bootstrap; every later navigation uses the
    // shared beforeEach guard above.
    const currentRoute = this.$router.currentRoute?.value;
    if (currentRoute) {
      const result = await enforceAccountRoute(currentRoute, this);
      if (result !== true) await this.$router.replace(result);
    }
  },
};

const compareVersions = (current: string, required: string) => {
  const currentParts = current.split(".").map((part) => Number(part) || 0);
  const requiredParts = required.split(".").map((part) => Number(part) || 0);
  const length = Math.max(currentParts.length, requiredParts.length);

  for (let index = 0; index < length; index++) {
    const currentPart = currentParts[index] || 0;
    const requiredPart = requiredParts[index] || 0;

    if (currentPart > requiredPart) return 1;
    if (currentPart < requiredPart) return -1;
  }

  return 0;
};
